import Text from '../../atoms/Text';
import Input from '../../atoms/Input';
import Button from '../../atoms/Button';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { SubmitHandler, useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import LITERALS from '../../../shared/constants/literals';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { showAlert, AlertPayload } from '../../../redux/slices/alert.slice';
import { ALERT_TYPE } from '../Alert';
import { msgToolTipPassword } from '../../../shared/constants/SatLinkMessages';
import { SATEFirmaForm } from '../SATLinkingForm';
import InputFileNew from '../../atoms/InputFileNew';
import { uploadEFirmaNew, uploadEFirmaPolling } from '../../../services/taxes.service';
import { CaretCircleLeft, CheckCircle, Clock } from '@phosphor-icons/react';
import { LINKS } from '../../../shared/constants/links';
import { TaxProfile } from '../../../@types/TaxProfile';
import { CircularProgress } from '@mui/material';

interface Props {
	onCallbackSuccess?: (value: any) => void;
	onCallbackError?: (error: any) => void;
	viewTitle?: boolean;
	isModal?: boolean;
	taxProfile?: TaxProfile | null;
}

const schemaEFirma = yup
	.object({
		key: yup.mixed().required(LITERALS.REQUIRED_FIELD),
		cert: yup.mixed().required(LITERALS.REQUIRED_FIELD),
		password: yup.string().required(LITERALS.REQUIRED_FIELD),
		// .matches(REGEX.nameRegex, LITERALS.MIN_LENGTH_PASSWORD)
		// .min(8, LITERALS.MIN_LENGTH_PASSWORD),
	})
	.required(LITERALS.REQUIRED_FIELD);

const UploadESignature = ({
	onCallbackSuccess,
	onCallbackError,
	viewTitle = true,
	isModal = false,
	taxProfile,
}: Props) => {
	const {
		control: controlEFirma,
		handleSubmit: handleSubmitEFirma,
		formState: { errors: errorsEFirma },
	} = useForm<SATEFirmaForm>({
		resolver: yupResolver(schemaEFirma),
		defaultValues: {
			cert: undefined,
			key: undefined,
			password: '',
		},
	});

	const [isLoading, setIsLoading] = useState(false);
	const [_pollingId, setPollingId] = useState<string | null>(null);
	const [timer, setTimer] = useState(0);
	const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
	const userProfile = useSelector((state: RootState) => state.profile.user);
	const fiscalDocuments = useSelector(
		(state: RootState) => state.taxProfile.links,
	);
	const { tokenSession } = useSelector((state: RootState) => state.auth);

	const dispatch = useDispatch();

	const showAlertTop = ({ title, type, description }: AlertPayload) => {
		const alert = {
			type,
			title,
			description,
		};
		dispatch(showAlert(alert));
	};

	const [step, setStep] = useState(0);

	// Función de polling para verificar el estado de la subida
	const pullingEFirmaHandler = useCallback(
		async (uploadId: string) => {
			try {
				const response = await uploadEFirmaPolling(uploadId, tokenSession);
				
				// Manejar la respuesta basada en currentState
				switch (response.currentState) {
					case 'VALIDATION_SUCCESSFUL_INGEST_TRIGGERED':
						// Proceso completado exitosamente
						setIsLoading(false);
						if (timerInterval) {
							clearInterval(timerInterval);
							setTimerInterval(null);
						}
						setTimer(0);
						setPollingId(null);
						onCallbackSuccess?.(response);
						showAlertTop({
							title: 'Éxito',
							type: ALERT_TYPE.SUCCESS,
							description: 'Tu e.firma ha sido vinculada correctamente',
						});
						break;

					case 'VALIDATION_FAILED':
					case 'ERROR':
					case 'FAILED':
						// Proceso falló
						setIsLoading(false);
						if (timerInterval) {
							clearInterval(timerInterval);
							setTimerInterval(null);
						}
						setTimer(0);
						setPollingId(null);
						showAlertTop({
							title: 'Error',
							type: ALERT_TYPE.ERROR,
							description: response.message || 'Error al procesar tu e.firma',
						});
						onCallbackError?.(response);
						break;

					default:
						// Continuar el polling para otros estados (en progreso)
						setTimeout(() => {
							pullingEFirmaHandler(uploadId);
						}, 2000);
						break;
				}
			} catch (error) {
				setIsLoading(false);
				if (timerInterval) {
					clearInterval(timerInterval);
					setTimerInterval(null);
				}
				setTimer(0);
				setPollingId(null);
				showAlertTop({
					title: 'Error',
					type: ALERT_TYPE.ERROR,
					description: 'Error al verificar el estado de tu e.firma',
				});
				onCallbackError?.(error);
			}
		},
		[tokenSession, timerInterval, onCallbackSuccess, onCallbackError],
	);

	useEffect(() => {
		let interval: NodeJS.Timeout | null = null;

		if (isLoading) {
			setTimer(0);
			interval = setInterval(() => {
				setTimer((prevTimer) => prevTimer + 1);
			}, 1000);
			setTimerInterval(interval);
		} else {
			if (timerInterval) {
				clearInterval(timerInterval);
				setTimerInterval(null);
			}
			setTimer(0);
		}

		return () => {
			if (interval) {
				clearInterval(interval);
			}
			if (timerInterval) {
				clearInterval(timerInterval);
			}
		};
	}, [isLoading]);

	const onSubmitEFirma: SubmitHandler<SATEFirmaForm> = async (data) => {
		if (!userProfile?.id || isLoading) return;
		if (!data.cert || !data.key || !data.password) {
			showAlertTop({
				title: 'Error',
				type: ALERT_TYPE.ERROR,
				description: LITERALS.REQUIRED_FIELDS,
			});
			return;
		}

		try {
			setIsLoading(true);
			
			// Obtener el RFC de las credenciales fiscales vinculadas o del taxProfile
			const rfc = fiscalDocuments?.links?.rfc || taxProfile?.links?.rfc || '';
			
			if (!rfc) {
				setIsLoading(false);
				showAlertTop({
					title: 'Error',
					type: ALERT_TYPE.ERROR,
					description: 'No se encontró el RFC asociado. Debes vincular primero tu RFC y contraseña.',
				});
				return;
			}
			
			const body = {
				cert: data.cert,
				key: data.key,
				password: data.password,
				userId: userProfile.id,
				rfc: rfc,
			};

			const resp = await uploadEFirmaNew(body, tokenSession);
			
			if (resp?.id || resp?.uploadId || resp?.workflowId || resp?.WorkflowId) {
				const uploadId = resp.id || resp.uploadId || resp.workflowId || resp.WorkflowId;
				setPollingId(uploadId);
				// Iniciar el polling después de 3 segundos
				setTimeout(() => {
					pullingEFirmaHandler(uploadId);
				}, 3000);
			} else if (resp?.statusCode === 422 || resp?.error) {
				setIsLoading(false);
				showAlertTop({
					title: 'Error',
					type: ALERT_TYPE.ERROR,
					description: resp.detail?.msg || resp.message || 'Error al procesar tu e.firma',
				});
				onCallbackError?.(resp);
			} else {
				setIsLoading(false);
				showAlertTop({
					title: 'Error',
					type: ALERT_TYPE.ERROR,
					description: 'No se recibió una respuesta válida del servidor',
				});
			}
		} catch (error: any) {
			setIsLoading(false);
			if (error?.response?.data?.detail?.msg || error?.response?.data?.message) {
				showAlertTop({
					title: 'Error',
					type: ALERT_TYPE.ERROR,
					description: error.response.data.detail?.msg || error.response.data.message,
				});
			} else {
				showAlertTop({
					title: 'Error',
					type: ALERT_TYPE.ERROR,
					description: LITERALS.UPLOAD_E_SIGNATURE_ERROR,
				});
			}
			onCallbackError?.(error);
		}
	};

	const cancelUploadHandler = () => {
		setIsLoading(false);
		if (timerInterval) {
			clearInterval(timerInterval);
			setTimerInterval(null);
		}
		setTimer(0);
		setPollingId(null);
	};

	if (isLoading) {
		return (
			<div className='p-6'>
				<div className='flex flex-col items-center'>
					<div className='relative w-[60px] h-[60px] mx-auto mb-4 bg-[#F4F9FE] rounded-full flex items-center justify-center'>
						<CircularProgress
							size={60}
							thickness={2}
							sx={{
								position: 'absolute',
								top: 0,
								left: 0,
								color: '#1790EC',
							}}
						/>
						<CheckCircle
							size={24}
							color='#1790EC'
							weight='duotone'
						/>
					</div>
					<div className='flex flex-col items-center justify-center w-[350px]'>
						<Text
							size='caption'
							weight='light'
							sx='mb-4 flex items-center gap-1'
						>
							<Clock size={16} />
							{Math.floor(timer / 60)}:
							{String(Math.floor(timer % 60)).padStart(2, '0')}
						</Text>
						<Text
							size='body-3'
							weight='regular'
						>
							Procesando tu e.firma
						</Text>
						<Text
							size='mini-1'
							weight='light'
							color='medium'
							sx='text-center mt-2'
						>
							Estamos validando y procesando tu e.firma. Este proceso puede tomar unos minutos.
						</Text>
					</div>
				</div>
				<div className='flex justify-center mt-4'>
					<Button
						variant='text'
						label='Cancelar'
						onClick={cancelUploadHandler}
						labelColor='primary'
					/>
				</div>
			</div>
		);
	}

	return (
		<div>
			{step === 0 && (
				<div className='p-2'>
					{viewTitle && (
						<Text
							weight='bold'
							color='medium'
							size='body-1'
							sx='mb-5'
						>
							Vincula tu e.Firma
						</Text>
					)}
					<form onSubmit={handleSubmitEFirma(onSubmitEFirma)}>
						<div className='flex flex-col gap-4'>
							<Text size='m'>Certificado</Text>
							<InputFileNew
								control={controlEFirma}
								name='cert'
								accept='.cer'
								errorMessage={errorsEFirma.cert?.message as string}
								label='Sube tu certificado (.cer)'
							/>
							<Text size='m'>Clave privada</Text>
							<InputFileNew
								control={controlEFirma}
								name='key'
								accept='.key'
								errorMessage={errorsEFirma.key?.message as string}
								label='Sube tu clave privada (.key)'
							/>
							<Input
								label='Contraseña clave privada'
								showTooltip
								tooltipMessage={msgToolTipPassword}
								control={controlEFirma}
								name='password'
								type='password'
								placeholder='Contraseña clave privada'
								errorMessage={errorsEFirma.password?.message}
								maxLength={50}
								rules={{
									required: true,
								}}
							/>

							<div className='flex flex-col lg:flex-row justify-between gap-4'>
								<Button
									variant='outline'
									sx='w-full lg:w-60 mt-2 lg:mt-0 border-0 !shadow-none hover:!bg-transparent'
									onClick={() => setStep(1)}
									label='No tengo mi e.Firma'
								/>

								<Button
									sx='w-full lg:w-60'
									loading={isLoading}
									disabled={
										!!errorsEFirma.key ||
										!!errorsEFirma.cert ||
										!!errorsEFirma.password ||
										isLoading
									}
									label={
										taxProfile?.statusCode === 404
											? 'Vincular mi e.Firma'
											: 'Cargar mi e.Firma'
									}
									type='submit'
								/>
							</div>
						</div>
					</form>
				</div>
			)}
			{step === 1 && (
				<div>
					{isModal && (
						<div className='absolute left-2 top-2'>
							<CaretCircleLeft
								weight='fill'
								color='var(--h-primary)'
								width={24}
								height={24}
								className='cursor-pointer'
								onClick={() => setStep(0)}
							/>
						</div>
					)}

					<div className='flex flex-col w-full h-[600px] md:h-[780px] mt-4'>
						{!isModal && (
							<CaretCircleLeft
								weight='fill'
								color='var(--h-primary)'
								width={24}
								height={24}
								className='cursor-pointer mb-2'
								onClick={() => setStep(0)}
							/>
						)}
						<Text
							size='s'
							sx='text-center bg-h-gray-10 flex justify-center py-2 z-10'
							color='medium'
						>
							Aviso: SATID es una página ajena a Heru.
						</Text>
						<iframe
							title='Embedded Page'
							width='100%'
							height='100%'
							src={LINKS.SAT_ID_LINK}
							allowFullScreen
						></iframe>
					</div>
				</div>
			)}
		</div>
	);
};

export default UploadESignature;
